import React, { useEffect, useMemo, useState } from "react";
import {
  Target,
  Building2,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  TrendingDown,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

// =============================================================================
// Ads Quality (/ads-quality)
// =============================================================================
// What Meta Ads Manager structurally cannot show: Meta doesn't have our form.
// It knows "a lead cost $X" but not WHO that lead is.
//
// The landing form asks for company + project size, so we compute the cost of a
// LARGE lead (developer with a 200+ apartment project) instead of a blended
// average. Raw CPL lies: a "cheap" campaign can farm 1-50 unit hobbyists while
// an "expensive" one brings 500+ unit developers.
//
// Backend: GET /ads-quality/overview?date_from=&date_to=  (backend/ads_quality.py)
// Sources: meta_ads_insights (spend) ⨝ landing_leads (lead quality); campaign
// names are normalized on both sides.
// =============================================================================

const SIZE_META = {
  "1-50": { label: "1–50", tone: "text-zinc-400", bar: "bg-zinc-500" },
  "50-200": { label: "50–200", tone: "text-sky-400", bar: "bg-sky-500" },
  "200-500": { label: "200–500", tone: "text-emerald-400", bar: "bg-emerald-500" },
  "500+": { label: "500+", tone: "text-emerald-300", bar: "bg-emerald-400" },
  unknown: { label: "n/a", tone: "text-zinc-500", bar: "bg-zinc-700" },
};

const RANGES = [
  { key: "30", label: "30 days", days: 30 },
  { key: "90", label: "90 days", days: 90 },
  { key: "365", label: "Year", days: 365 },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function money(v, currency) {
  if (v === null || v === undefined) return "—";
  const sym = currency === "USD" ? "$" : "";
  return `${sym}${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export default function AdsQualityPage() {
  const [range, setRange] = useState("90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async (rangeKey = range) => {
    setLoading(true);
    setError("");
    try {
      const days = RANGES.find((r) => r.key === rangeKey)?.days || 90;
      const res = await api.get("/ads-quality/overview", {
        params: { date_from: isoDaysAgo(days) },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load ads quality data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const totals = data?.totals || {};
  const currency = totals.currency;

  // Campaigns with spend OR leads, sorted by cost per large lead — that's the
  // question this page exists to answer.
  const rows = useMemo(() => {
    const list = (data?.campaigns || []).filter((c) => (c.spend || 0) > 0 || c.leads > 0);
    return [...list].sort((a, b) => {
      const av = a.cost_per_large ?? Infinity;
      const bv = b.cost_per_large ?? Infinity;
      if (av !== bv) return av - bv;
      return (b.large_leads || 0) - (a.large_leads || 0);
    });
  }, [data]);

  const best = rows.find((r) => r.cost_per_large != null);

  return (
    <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Target className="w-6 h-6 text-emerald-400" />
            Ads Quality
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Meta only knows "a lead cost $X" — not <b>who</b> that lead is. Our landing form asks for
            project size, so we price a <b>large</b> developer (200+ apartments) instead of a blended
            average.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={range === r.key ? "default" : "outline"}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-zinc-400 py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Crunching…
        </div>
      )}

      {data && (
        <>
          {data.mixed_currency && (
            <div className="flex items-center gap-2 text-sm text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4" />
              Ad accounts use different currencies ({data.currencies.join(", ")}) — money is not
              blended, only counts are shown.
            </div>
          )}

          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi label="Spend" value={money(totals.spend, currency)} />
            <Kpi label="Form leads" value={totals.form_leads ?? "—"} />
            <Kpi
              label="Large (200+)"
              value={totals.large_leads ?? "—"}
              sub={totals.large_share != null ? `${totals.large_share}% of all` : null}
              accent
            />
            <Kpi label="CPL (blended)" value={money(totals.cpl, currency)} />
            <Kpi label="Cost per large lead" value={money(totals.cost_per_large, currency)} accent hero />
            <Kpi
              label="Unattributed"
              value={(totals.untagged_leads ?? 0) + (totals.macro_leak_leads ?? 0)}
              sub={totals.macro_leak_leads ? `${totals.macro_leak_leads} from a broken macro` : null}
              warn={(totals.untagged_leads ?? 0) + (totals.macro_leak_leads ?? 0) > 0}
            />
          </div>

          {/* Size distribution */}
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
                All leads by project size
              </div>
              <SizeBar sizes={data.size_totals} total={totals.form_leads} />
            </CardContent>
          </Card>

          {best && (
            <div className="text-sm text-zinc-400">
              Best cost per large lead —{" "}
              <span className="text-emerald-400 font-medium">{best.campaign}</span>:{" "}
              {money(best.cost_per_large, currency)} per 200+ developer.
            </div>
          )}

          {/* Table */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-800">
                    <th className="text-left font-medium px-3 py-2 w-8"></th>
                    <th className="text-left font-medium px-3 py-2">Campaign</th>
                    <th className="text-right font-medium px-3 py-2">Spend</th>
                    <th className="text-right font-medium px-3 py-2">Leads</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[190px]">By size</th>
                    <th className="text-right font-medium px-3 py-2">Large</th>
                    <th className="text-right font-medium px-3 py-2">CPL</th>
                    <th className="text-right font-medium px-3 py-2 whitespace-nowrap">
                      Cost per large
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const isOpen = !!expanded[c.campaign];
                    return (
                      <React.Fragment key={c.campaign}>
                        <tr
                          className="border-b border-zinc-800/60 hover:bg-zinc-500/5 cursor-pointer"
                          onClick={() =>
                            setExpanded((p) => ({ ...p, [c.campaign]: !p[c.campaign] }))
                          }
                        >
                          <td className="px-3 py-2 text-zinc-500">
                            {isOpen ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </td>
                          <td className="px-3 py-2 max-w-[320px]">
                            <div className="truncate" title={c.campaign}>
                              {c.campaign}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {money(c.spend, c.currency)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {c.leads}
                            {c.meta_reported_leads > 0 && c.meta_reported_leads !== c.leads && (
                              <span
                                className="text-zinc-500 ml-1"
                                title={`Meta counted ${c.meta_reported_leads}`}
                              >
                                /{c.meta_reported_leads}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <SizeBar sizes={c.by_size} total={c.leads} compact />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={c.large_leads ? "text-emerald-400 font-medium" : ""}>
                              {c.large_leads}
                            </span>
                            {c.large_share != null && (
                              <span className="text-zinc-500 text-xs ml-1">{c.large_share}%</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                            {money(c.cpl, c.currency)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                            {c.cost_per_large != null ? (
                              <span className="text-emerald-400">
                                {money(c.cost_per_large, c.currency)}
                              </span>
                            ) : (
                              <span className="text-zinc-600">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-zinc-800/60 bg-zinc-500/[0.03]">
                            <td></td>
                            <td colSpan={7} className="px-3 py-3">
                              <ExpandedDetail c={c} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!rows.length && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-zinc-500">
                        No spend and no leads in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <p className="text-xs text-zinc-500">
            Leads = submitted forms only (those carry project size). "Leads X/Y": X actually reached
            the landing, Y is what Meta counted. Campaign names are normalized (URL encoding, "Test"
            suffixes) — otherwise one campaign splits into several rows.
          </p>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent, hero, warn }) {
  return (
    <Card className={hero ? "border-emerald-500/40 bg-emerald-500/[0.06]" : ""}>
      <CardContent className="pt-4 pb-3">
        <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
        <div
          className={`text-2xl font-semibold mt-1 tabular-nums ${
            warn ? "text-amber-400" : accent ? "text-emerald-400" : ""
          }`}
        >
          {value}
        </div>
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SizeBar({ sizes, total, compact }) {
  const order = ["1-50", "50-200", "200-500", "500+", "unknown"];
  const sum = order.reduce((a, k) => a + (sizes?.[k] || 0), 0) || total || 0;
  if (!sum) return <span className="text-zinc-600 text-xs">—</span>;
  return (
    <div>
      <div className={`flex ${compact ? "h-1.5" : "h-2.5"} rounded overflow-hidden bg-zinc-800`}>
        {order.map((k) => {
          const n = sizes?.[k] || 0;
          if (!n) return null;
          return (
            <div
              key={k}
              className={SIZE_META[k].bar}
              style={{ width: `${(n / sum) * 100}%` }}
              title={`${SIZE_META[k].label}: ${n}`}
            />
          );
        })}
      </div>
      {!compact && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
          {order.map((k) => {
            const n = sizes?.[k] || 0;
            if (!n) return null;
            return (
              <span key={k} className={SIZE_META[k].tone}>
                <span className={`inline-block w-2 h-2 rounded-sm mr-1 ${SIZE_META[k].bar}`} />
                {SIZE_META[k].label}: <b>{n}</b>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExpandedDetail({ c }) {
  const creatives = Object.entries(c.creatives || {}).sort((a, b) => b[1] - a[1]);
  const landings = Object.entries(c.landings || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
          Lead companies ({c.companies?.length || 0})
        </div>
        {c.companies?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {c.companies.map((co) => (
              <span
                key={co}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-zinc-700 text-zinc-300"
              >
                <Building2 className="w-3 h-3 text-zinc-500" />
                {co}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-xs text-zinc-600">company field left empty</div>
        )}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
          Creatives (utm_content)
        </div>
        {creatives.length ? (
          <ul className="text-xs space-y-1">
            {creatives.map(([k, n]) => (
              <li key={k} className="flex justify-between gap-2">
                <span className="truncate text-zinc-300">{k}</span>
                <span className="tabular-nums text-zinc-500">{n}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-zinc-600">—</div>
        )}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Landings</div>
        {landings.length ? (
          <ul className="text-xs space-y-1">
            {landings.map(([k, n]) => (
              <li key={k} className="flex justify-between gap-2">
                <span className="uppercase text-zinc-300">{k}</span>
                <span className="tabular-nums text-zinc-500">{n}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-zinc-600">—</div>
        )}
        {c.meta_reported_leads > c.leads && (
          <div className="mt-3 flex items-start gap-1.5 text-xs text-amber-400/90">
            <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Meta counted {c.meta_reported_leads} but {c.leads} reached the landing — attribution gap.
          </div>
        )}
      </div>
    </div>
  );
}
